import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { JET_PROGRAMS, JET_CARD_CATEGORIES, money } from "@/lib/format";
import { Field, inputCls } from "@/components/LegsEditor";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const JET_HOURS = [25, 50, 75, 100];
const PER_25 = {
  light: { preferred: 201000, signature: 209000, elite: 259000 },
  midsize: { preferred: 240237, signature: 249000, elite: 299000 },
  super_midsize: { preferred: 303125, signature: 314800, elite: 389000 },
  heavy: { preferred: 370312, signature: 386200, elite: 480000 },
};
const rateFor = (f) => (f.program && f.category ? PER_25[f.category][f.program] / 25 : null);
const standardFor = (f) => (f.hours && rateFor(f) ? rateFor(f) * Number(f.hours) : null);
const priceFor = (f) => {
  if (!standardFor(f)) return null;
  if (f.pricing === "bonus_hours") return Number(f.paid_hours) > 0 ? rateFor(f) * Number(f.paid_hours) : null;
  if (f.pricing === "custom_price") return Number(f.custom_price) > 0 ? Number(f.custom_price) : null;
  return standardFor(f);
};
const PRICING = [{ value: "standard", label: "Standard price" }, { value: "bonus_hours", label: "Bonus hours" }, { value: "custom_price", label: "Custom price" }];
const BLANK = { full_name: "", email: "", phone: "", company: "", titled_to: "full_name", hours: "", program: "", category: "", extra_terms: "", pricing: "standard", paid_hours: "", custom_price: "" };

function Choice({ value, options, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button type="button" key={o.value} onClick={() => onChange(o.value)} disabled={o.disabled}
          className={`frame-btn ${value === o.value ? "gold" : ""} disabled:opacity-40`} data-testid={`${testid}-${o.value}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PriceSummary({ f }) {
  const price = priceFor(f);
  if (!standardFor(f)) return null;
  const detail = f.pricing === "bonus_hours" ? `${f.hours} hours for the price of ${f.paid_hours || "—"} hours (${money(rateFor(f))}/hr × ${f.paid_hours || "—"})`
    : f.pricing === "custom_price" ? `Custom price for ${f.hours} hours${price ? ` (${money(price / Number(f.hours))} per hour)` : ""}`
      : `Prorated: ${money(rateFor(f))} per hour × ${f.hours} hours = ${money(price)}`;
  return (
    <div className="lux-card m-card p-5 flex flex-wrap items-baseline gap-x-4 gap-y-1" data-testid="jc-price-summary">
      <span className="text-[0.6rem] tracking-[0.22em] uppercase text-zinc-500">Jet Card price</span>
      <span className="font-serif-display text-3xl text-white" data-testid="jc-price-value">{price ? money(price) : "—"}</span>
      <span className="text-xs text-zinc-500">{f.hours} hours · {JET_PROGRAMS[f.program].label} · term {f.program === "preferred" ? "24" : "30"} months</span>
      <span className="basis-full text-xs text-zinc-400" data-testid="jc-proration">{detail}</span>
      {f.pricing !== "standard" && (
        <span className="basis-full text-xs text-[#8A6D2B]" data-testid="jc-custom-deal-note">Standard price {money(standardFor(f))}. Custom deals are reviewed and acknowledged by an admin.</span>
      )}
    </div>
  );
}

export function JetCardThanks({ onAgain }) {
  return (
    <div className="lux-card m-card p-8 sm:p-10 text-center max-w-2xl" data-testid="jet-card-thanks">
      <CheckCircle2 size={34} className="mx-auto text-[#C6A55C]" />
      <h2 className="font-serif-display text-3xl text-white mt-4">Thank you</h2>
      <p className="text-zinc-300 mt-3 leading-relaxed">Your Jet Card agreement request has been sent. Your DocuSign will be prepared shortly after it is received.</p>
      <button type="button" onClick={onAgain} className="frame-btn mt-7" data-testid="jet-card-new-request-btn">Submit another request</button>
    </div>
  );
}

export default function JetCardForm({ onSubmitted, brokers = null }) {
  const api = useApi();
  const [f, setF] = useState(BLANK);
  const [brokerId, setBrokerId] = useState("");
  const [saving, setSaving] = useState(false);
  const [custom, setCustom] = useState(false);
  const pickHours = (v) => { setCustom(v === "custom"); setF((s) => ({ ...s, hours: v === "custom" ? "" : v })); };
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (brokers && !brokerId) return toast.error("Choose the broker this request is for");
    if (!f.hours || !f.program || !f.category) return toast.error("Choose the Jet Card hours, type and category");
    if (!Number.isInteger(Number(f.hours)) || Number(f.hours) < 1) return toast.error("Custom hours must be a whole number");
    if (f.titled_to === "company" && !f.company.trim()) return toast.error("Enter the company name to title the contract to the company");
    if (f.pricing === "bonus_hours" && !(Number.isInteger(Number(f.paid_hours)) && Number(f.paid_hours) >= 1 && Number(f.paid_hours) < Number(f.hours))) return toast.error("Hours paid for must be a whole number fewer than the hours given");
    if (f.pricing === "custom_price" && !(Number(f.custom_price) > 0)) return toast.error("Enter the custom total price");
    setSaving(true);
    try {
      const deal = { paid_hours: f.pricing === "bonus_hours" ? Number(f.paid_hours) : null, custom_price: f.pricing === "custom_price" ? Number(f.custom_price) : null };
      const { data } = await api.post("/jet-card-requests", { ...f, ...deal, hours: Number(f.hours), broker_id: brokers ? brokerId : undefined });
      setF(BLANK);
      setCustom(false);
      setBrokerId("");
      onSubmitted(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-3xl space-y-6" data-testid="jet-card-form">
      {brokers && (
        <Field label="On behalf of broker">
          <Select value={brokerId} onValueChange={setBrokerId}>
            <SelectTrigger className={inputCls} data-testid="jc-broker-select"><SelectValue placeholder="Choose a broker" /></SelectTrigger>
            <SelectContent>{brokers.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`jc-broker-${b.id}`}>{b.name || b.email}{b.name && b.email ? ` · ${b.email}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name"><Input className={inputCls} value={f.full_name} onChange={set("full_name")} required data-testid="jc-full-name-input" /></Field>
        <Field label="Email"><Input type="email" className={inputCls} value={f.email} onChange={set("email")} required data-testid="jc-email-input" /></Field>
        <Field label="Phone number"><Input type="tel" className={inputCls} value={f.phone} onChange={set("phone")} required data-testid="jc-phone-input" /></Field>
        <Field label="Company (if applicable)"><Input className={inputCls} value={f.company} onChange={set("company")} data-testid="jc-company-input" /></Field>
      </div>
      <div>
        <p className="text-[0.6rem] tracking-[0.22em] uppercase text-zinc-500 mb-2">Contract titled to</p>
        <Choice value={f.titled_to} onChange={set("titled_to")} testid="jc-titled-to"
          options={[{ value: "full_name", label: f.full_name.trim() || "Full name" }, { value: "company", label: f.company.trim() || "Company", disabled: !f.company.trim() }]} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.pricing === "bonus_hours" ? "Hours given" : "Jet Card hours"}>
          <Select value={custom ? "custom" : String(f.hours)} onValueChange={pickHours}>
            <SelectTrigger className={inputCls} data-testid="jc-hours-select"><SelectValue placeholder="Choose hours" /></SelectTrigger>
            <SelectContent>
              {JET_HOURS.map((h) => <SelectItem key={h} value={String(h)} data-testid={`jc-hours-${h}`}>{h} hours</SelectItem>)}
              <SelectItem value="custom" data-testid="jc-hours-custom">Custom hours</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {custom && (
          <Field label="Custom hours">
            <Input className={inputCls} type="number" min="1" step="1" value={f.hours} onChange={set("hours")} placeholder="e.g. 40" data-testid="jc-custom-hours-input" />
          </Field>
        )}
      </div>
      <div>
        <p className="text-[0.6rem] tracking-[0.22em] uppercase text-zinc-500 mb-2">Jet Card type</p>
        <Choice value={f.program} onChange={set("program")} testid="jc-program" options={Object.entries(JET_PROGRAMS).map(([value, p]) => ({ value, label: p.label }))} />
      </div>
      <div>
        <p className="text-[0.6rem] tracking-[0.22em] uppercase text-zinc-500 mb-2">Jet Card category</p>
        <Choice value={f.category} onChange={set("category")} testid="jc-category" options={JET_CARD_CATEGORIES} />
      </div>
      <div>
        <p className="text-[0.6rem] tracking-[0.22em] uppercase text-zinc-500 mb-2">Pricing</p>
        <Choice value={f.pricing} onChange={set("pricing")} testid="jc-pricing" options={PRICING} />
        {f.pricing !== "standard" && (
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {f.pricing === "bonus_hours" ? (
              <Field label="Hours paid for">
                <Input className={inputCls} type="number" min="1" step="1" value={f.paid_hours} onChange={set("paid_hours")} placeholder="e.g. 50" data-testid="jc-paid-hours-input" />
              </Field>
            ) : (
              <Field label="Custom total price (USD)">
                <Input className={inputCls} type="number" min="1" step="0.01" value={f.custom_price} onChange={set("custom_price")} placeholder="e.g. 480000" data-testid="jc-custom-price-input" />
              </Field>
            )}
          </div>
        )}
      </div>
      {f.program && f.category && <PriceSummary f={f} />}
      <Field label="Extra wording for the contract (optional)">
        <Textarea className="bg-[#0d0f14] border-white/10 min-h-[120px]" value={f.extra_terms} onChange={set("extra_terms")} maxLength={4000}
          placeholder="Any additional terms or information to add to the contract…" data-testid="jc-extra-terms-input" />
      </Field>
      <button type="submit" disabled={saving} className="frame-btn gold" data-testid="jc-submit-btn">
        <Send size={14} /> {saving ? "Sending…" : "Request Jet Card agreement"}
      </button>
    </form>
  );
}
